/**
 * "How is this computed?" — the pressure formula with the arithmetic of
 * one row, the smoothing prior, and what trend and outcome mean.
 * Transparency is the point: every number on the map can be redone by
 * hand from this.
 */
import type { HotspotPrior, HotspotRow } from "@iamu/shared";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { formatPressure, plural } from "./hotspot-vocabulary";

/** "10 × (164 + 10 × 3.90) / (33 + 10) = 47.2" */
export function pressureArithmetic(
  row: Pick<HotspotRow, "weightSum" | "exposureSteps" | "pressure" | "status">,
  prior: HotspotPrior,
): string {
  if (row.pressure == null) return "Not walked in this window: no pressure.";
  if (row.status === "clear") {
    return `No unresolved problems in ${plural(row.exposureSteps, "step")}: pressure 0.`;
  }
  return `10 × (${row.weightSum} + ${prior.k} × ${prior.mu.toFixed(2)}) / (${
    row.exposureSteps
  } + ${prior.k}) = ${formatPressure(row.pressure)}`;
}

export function PressureExplanation({
  row,
  prior,
}: {
  row: HotspotRow | null;
  prior: HotspotPrior;
}) {
  return (
    <div className="space-y-3 text-sm text-foreground">
      <p>
        <strong className="font-semibold">Pressure</strong> = 10 × (Σ priority +
        k × μ) / (steps walked + k)
      </p>
      {row && (
        <p className="rounded-sm bg-muted px-2 py-1.5 font-mono text-xs tabular-nums">
          <span className="font-sans font-medium">{row.label}: </span>
          {pressureArithmetic(row, prior)}
        </p>
      )}
      <ul className="list-disc space-y-1.5 pl-4">
        <li>
          Σ priority adds the queue's priority score of each problem that is
          still open or awaiting a re-run
          {row
            ? ` (here ${plural(row.openProblems + row.awaitingRerun, "problem")})`
            : ""}
          . A fix counts in full until a re-run verifies it. The score already
          includes reach and recurrence, so nothing is multiplied on top.
        </li>
        <li>
          k = {prior.k} steps and μ = {prior.mu.toFixed(2)}, the project's
          priority per step walked in this window. Areas walked only briefly are
          pulled toward μ, so one bad step can't top the list. Areas walked with
          no unresolved problems read 0 ("clear"): the prior only tempers areas
          that have problems.
        </li>
        <li>
          Strengths (helps findings) never add pressure. The human-test outcome
          is its own column: the latest run of each scenario.
        </li>
        <li>
          Trend: one point per run, by start time, as priority per 10 steps in
          that run (not smoothed). The direction compares the latest run with
          the average of the earlier ones.
        </li>
      </ul>
    </div>
  );
}

export function PressureFormulaPopover({
  row,
  prior,
}: {
  row: HotspotRow | null;
  prior: HotspotPrior;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="px-2 underline">
          How is this computed?
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[26rem]">
        <PressureExplanation row={row} prior={prior} />
      </PopoverContent>
    </Popover>
  );
}
